import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { AgGridReact } from "ag-grid-react";
import { blue, orange } from "@mui/material/colors";
import { AgTooltipBox, AG_TOOLTIP_HEADING_STYLE } from "@plannel/components/rechart/AgStyleTooltip";
import { FONT_FAMILY_CSS } from "@plannel/utils/font-util";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridDatePicker from "@plannel/components/GridDatePicker";
import StatusChip from "./StatusChip";
import scenarioService from "@plannel/services/ai/scenarioService";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { C } from "@plannel/components/ai/aiTokens";
import {
  PRICE_AMT_FIELDS as AMT_FIELDS, PRICE_AMT_LABEL_KEYS as AMT_LABEL_KEYS, PRICE_AMT_WIDTHS, UC, dateStr, fmtAmt,
} from "./llmUsageUtil";

// 공식 가격 데이터에서 단가 후보를 받아 확인하는 창.
// 적용 시작일은 초깃값을 채워 두고 사용자가 고칠 수 있다(가격표 날짜는 재발행일이라 단가 적용일과 다를 수 있다).
// 저장 버튼이 선택 행을 바로 저장한다(단가 탭 그리드를 거치지 않음).
const STATUS_LABEL_KEYS = {
  new: "llmUsagePriceStatusNew",
  changed: "llmUsagePriceStatusChanged",
  same: "llmUsagePriceStatusSame",
  not_found: "llmUsagePriceStatusNotFound",
};
// 표에 넣을 의미가 있는 행만 선택·날짜 입력을 허용한다(같은 단가·조회 안 된 모델은 확인용 표시)
const APPLICABLE = ["new", "changed"];
// 호출 내역 탭과 같은 상태 칩 — 신규=파랑, 변경=주황, 조회 안 됨=중성(오류 아님). 변경 없음은 확인용이라 칩 없이 글자만
const STATUS_CHIP_COLORS = { new: blue[700], changed: orange[800], not_found: null };

// 생김새는 공용 AgTooltipBox 에 맡긴다 — 본체가 투명해 화살표가 안 보이므로 대상 바로 옆에 띄운다
const POPPER_PROPS = {
  tooltip: { sx: { bgcolor: "transparent", p: 0, m: "0 !important", maxWidth: "none" } },
  popper: { modifiers: [{ name: "offset", options: { offset: [0, 6] } }] },
};
const TIP_LABEL = { color: UC.text2 };
// 제공사마다 키 구성·순서가 달라 고정 순서로 맞춘다 — 행끼리 같은 위치에 같은 항목이 와야 비교된다
const SOURCE_INFO_ORDER = ["usagetype", "servicename", "model", "scope", "offer", "region"];
const sourceInfoRank = (k) => {
  const i = SOURCE_INFO_ORDER.indexOf(k);
  return i < 0 ? SOURCE_INFO_ORDER.length : i;
};
// 목록에 없는 키(다른 제공사)는 안정 정렬이라 원래 순서대로 뒤에 붙는다
const sourceInfoEntries = (info) => Object.entries(info || {})
  .filter(([, v]) => v != null && v !== "")
  .sort(([a], [b]) => sourceInfoRank(a) - sourceInfoRank(b));

// 조회 조건과 다른 지역에서 읽은 단가 — 상태와 별개 정보라 배지 옆 점선 태그로 늘 보이고, 올리면 두 지역을 비교한다
const OtherRegionTag = ({ queryRegion, region }) => {
  const { t } = useTranslation();
  return (
    <Tooltip arrow={false} placement="right" componentsProps={POPPER_PROPS}
      title={(
        // 제목 행이 두 열을 덮어 남는 폭은 값 열 오른쪽에만 — 값이 라벨 바로 옆에 붙는다
        <AgTooltipBox style={{ fontFamily: FONT_FAMILY_CSS, gridTemplateColumns: "max-content 1fr",
          columnGap: 16, rowGap: 4 }}>
          <div style={{ ...AG_TOOLTIP_HEADING_STYLE, fontWeight: 700 }}>{t("llmUsagePriceRegionNote")}</div>
          <span style={TIP_LABEL}>{t("llmUsagePriceRegionQuery")}</span>
          <span>{queryRegion}</span>
          <span style={TIP_LABEL}>{t("llmUsagePriceRegionFetched")}</span>
          <span style={{ fontWeight: 700 }}>{region}</span>
        </AgTooltipBox>
      )}>
      <Box component="span" sx={{
        display: "inline-flex", alignItems: "center", gap: 0.25, px: 0.5, borderRadius: 0.75,
        border: `1px dashed ${C.text3}`, color: C.text2, fontSize: FONT.CAPTION, lineHeight: "16px",
      }}>
        <PublicOutlinedIcon sx={{ fontSize: "1.1em" }} />
        {region}
      </Box>
    </Tooltip>
  );
};

const StatusBadge = ({ value, data }) => {
  const { t } = useTranslation();
  const label = t(STATUS_LABEL_KEYS[value] || "llmUsageNotSet");
  // 선택 가능한 그리드라 테두리를 살린다(선택 행의 연한 파랑 배경에 칩 윤곽이 묻히지 않게)
  const status = value in STATUS_CHIP_COLORS
    ? <StatusChip value={value} label={label} color={STATUS_CHIP_COLORS[value]} bordered />
    : <Box component="span" sx={{ color: C.text2, fontSize: FONT.BODY }}>{label}</Box>;
  if (!data?.other_region) return status;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {status}
      <OtherRegionTag queryRegion={data.query_region} region={data.other_region} />
    </Box>
  );
};

// 입력 가능한 행(신규·변경)만 달력 아이콘을 둔다 — 변경 없음 행의 읽기 전용 날짜와 구분되게
const EffectiveFromCell = ({ valueFormatted, data }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, height: "100%" }}>
    <span>{valueFormatted}</span>
    {APPLICABLE.includes(data?.status) && (
      <CalendarMonthOutlinedIcon sx={{ fontSize: FONT.BODY, color: C.accM, flexShrink: 0 }} />
    )}
  </Box>
);

// 라벨은 가격표 원래 필드명 그대로 — 가격표를 검색할 때 그대로 쓸 수 있게
const SourceInfoCell = ({ data }) => {
  const entries = sourceInfoEntries(data?.source_info);
  if (entries.length === 0) return null;
  return (
    // 아래 행을 덮는 위치라 셀을 벗어나면 바로 닫는다 — 안 닫히면 아래 행 셀에 올릴 수 없다
    <Tooltip arrow={false} disableInteractive placement="bottom-end" componentsProps={POPPER_PROPS}
      title={(
        <AgTooltipBox style={{ fontFamily: FONT_FAMILY_CSS, gridTemplateColumns: "max-content 1fr",
          columnGap: 16, rowGap: 4 }}>
          {entries.map(([k, v]) => (
            <Fragment key={k}>
              <span style={TIP_LABEL}>{k}</span>
              <span style={k === "usagetype" ? { fontWeight: 700 } : undefined}>{v}</span>
            </Fragment>
          ))}
        </AgTooltipBox>
      )}>
      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {entries[0][1]}
      </Box>
    </Tooltip>
  );
};

// 신규는 최초 호출일(이전 사용량이 단가 없이 남지 않게), 변경은 현재 시작일보다 늦은 가격표 적용일만(아니면 중복·무효)
const initialEffectiveFrom = (row) => {
  const cur = row.current?.effective_from || "";
  if (row.status === "same") return cur;
  if (row.status === "new") return row.first_used_dt || row.source_date || "";
  if (row.status === "changed" && row.source_date && (!cur || row.source_date > cur)) return row.source_date;
  return "";
};

const LlmUsagePriceSuggestDialog = ({ open, onClose, onSaved, notify }) => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const [sources, setSources] = useState([]);
  const [provider, setProvider] = useState("");
  const [region, setRegion] = useState("");
  // 직접 입력 칸은 입력 중에 조회하지 않는다 — Enter·포커스 이탈에서만 region 으로 확정
  const [regionInput, setRegionInput] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const requestSeq = useRef(0);

  const source = useMemo(() => sources.find((s) => s.provider_cd === provider), [sources, provider]);
  const regionOption = useMemo(() => (source?.options || []).find((o) => o.key === "region"), [source]);

  const defaultRegion = (list, p) =>
    (list.find((s) => s.provider_cd === p)?.options || []).find((o) => o.key === "region")?.default || "";

  // 제공사와 조건 기본값을 한 번에 바꿔 조회가 한 번만 나가게 한다
  const selectProvider = (list, p) => {
    const r = defaultRegion(list, p);
    setProvider(p); setRegion(r); setRegionInput(r);
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await scenarioService.llmUsagePriceSources();
        const list = r?.sources || [];
        setSources(list);
        selectProvider(list, list.find((s) => s.supported)?.provider_cd || list[0]?.provider_cd || "");
      } catch {
        setSources([]);
        notify("error", t("agentTraceLoadFail"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 화면 상단 필터와 같이 조건이 바뀌면 바로 조회한다(조회 버튼 없음)
  useEffect(() => {
    setRows([]); setSelected([]);
    if (!provider || !source?.supported) return;
    const seq = ++requestSeq.current;
    (async () => {
      setLoading(true);
      try {
        const r = await scenarioService.llmUsagePricesSuggest({ provider_cd: provider, region });
        if (seq !== requestSeq.current) return;          // 조건이 또 바뀌었으면 늦게 온 응답은 버린다
        setRows((r?.rows || []).map((row) => ({
          ...row, effective_from: initialEffectiveFrom(row),
          other_region: row.region && region && row.region !== region ? row.region : null,
          query_region: region,
        })));
        if (r?.source_error) notify("error", t("llmUsagePriceSourceError"));
        else if ((r?.rows || []).length === 0) notify("info", t("llmUsagePriceNoTarget"));
      } catch {
        if (seq !== requestSeq.current) return;
        notify("error", t("llmUsagePriceSourceError"));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, region, source?.supported]);

  // 선택한 행을 바로 저장한다 — 실패(중복·검증)하면 창을 닫지 않고 고칠 수 있게 둔다
  const save = async () => {
    const picked = rows.filter((r) => selected.includes(r.model_id));
    if (picked.length === 0 || saving) return;
    setSaving(true);
    try {
      await scenarioService.llmUsagePricesSave(picked.map((r) => ({
        id: null, ver_num: 0, provider_cd: provider, model_id: r.model_id, model_nm: r.model_nm || null,
        currency_cd: r.currency_cd || "USD", effective_from: dateStr(r.effective_from),
        ...Object.fromEntries(AMT_FIELDS.map((f) => [f, r[f] ?? null])),
      })));
      notify("success", t("llmUsagePriceSaved"));
      onClose();
      onSaved?.();
    } catch (e) {
      notify("error", e?.response?.status === 409 ? t("llmUsagePriceConflict") : t("llmUsagePriceInvalid"));
    } finally {
      setSaving(false);
    }
  };

  const columnDefs = useMemo(() => {
    // 현재값·취득값 4열씩 — 같은 열 이름을 그룹 헤더로 구분한다
    const amtColumn = (field, current) => ({
      colId: current ? `cur_${field}` : field,
      headerName: t(AMT_LABEL_KEYS[field]), width: PRICE_AMT_WIDTHS[field], type: ["rightAligned"], editable: false,
      valueGetter: (p) => (current ? p.data?.current?.[field] : p.data?.[field]),
      valueFormatter: (p) => (p.value == null ? "" : fmtAmt(p.value)),
    });
    return [
    { field: "model_id", headerName: t("agentTraceMetricModel"), minWidth: 260, flex: 1, editable: false },
    { field: "model_nm", headerName: t("llmUsageModelName"), width: 160, editable: false },
    { field: "status", headerName: t("status"), width: 180, editable: false, cellRenderer: StatusBadge,
      // 왼쪽 정렬 — 지역 태그가 붙는 행도 상태 글자 시작 위치가 같아 위아래로 훑기 쉽다
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-start" },
      valueFormatter: (p) => t(STATUS_LABEL_KEYS[p.value] || "llmUsageNotSet") },
    // 적용 시작일만 입력 대상 — 필수 표시는 MUI required 와 같은 "*"
    // popupGrid: 달력을 모달(z-index 1300) 위 전용 포털에 띄운다 — 없으면 모달 뒤에 가려진다
    { field: "effective_from", headerName: `${t("effStartDt")} *`, width: 140,
      editable: (p) => APPLICABLE.includes(p.data?.status),
      type: ["defaultValueParser"], cellEditor: GridDatePicker,
      cellEditorParams: { dateType: "day", popupGrid: true },
      // 한 번 클릭으로 달력을 연다(이 열만) — 더블클릭을 모르면 입력 칸인지 알기 어렵다
      onCellClicked: (p) => {
        if (p.colDef.editable(p)) p.api.startEditingCell({ rowIndex: p.rowIndex, colKey: "effective_from" });
      },
      valueFormatter: (p) => dateStr(p.value) || "",
      cellRenderer: EffectiveFromCell },
    { field: "first_used_dt", headerName: t("llmUsagePriceFirstUsed"), width: 130, editable: false },
    { field: "source_date", headerName: t("llmUsagePriceSourceDate"), width: 130, editable: false },
    { field: "currency_cd", headerName: t("currencyCd"), width: 100, editable: false },
    { headerName: t("llmUsagePriceFetched"), marryChildren: true,
      children: AMT_FIELDS.map((f) => amtColumn(f, false)) },
    { headerName: t("llmUsagePriceCurrent"), marryChildren: true,
      children: AMT_FIELDS.map((f) => amtColumn(f, true)) },
    // ag-grid tooltip 대신 MUI Tooltip 을 쓴다 — 편집 셀이 있는 그리드에서 에디터 툴팁 초기화가 오류를 낸다
    { colId: "source_info", headerName: t("llmUsagePriceSourceInfo"), width: 280, editable: false,
      cellRenderer: SourceInfoCell,
      valueGetter: (p) => sourceInfoEntries(p.data?.source_info).map(([k, v]) => `${k}: ${v}`).join(" · ") },
    ];
  }, [t]);

  const gridSetting = useMemo(() => {
    const base = DefaultGridSetting({ title: "llmUsagePriceFetch", viewName: "LLM_USAGE_PRICE_SUGGEST", columnDefs });
    return { ...base, defaultColDef: { ...base.defaultColDef, editable: false },
      // ag-grid 35 는 선택 가능 여부를 rowSelection 안에서만 읽는다(최상위 isRowSelectable 은 무시됨)
      rowSelection: { ...base.rowSelection, headerCheckbox: true, checkboxes: true,
        isRowSelectable: (node) => APPLICABLE.includes(node.data?.status) },
      // 날짜를 고치면 rowData 를 새로 만든다 — 행 식별자가 없으면 선택이 풀린다
      getRowId: (p) => p.data.model_id };
  }, [columnDefs]);

  const selectedRows = rows.filter((r) => selected.includes(r.model_id));
  const missingDate = selectedRows.some((r) => !dateStr(r.effective_from));

  return (
    <BaseConfigModal open={open} onClose={onClose} title={t("llmUsagePriceFetch")}
      subtitle={t("llmUsagePriceFetchNote")} width="min(1240px, 95vw)"
      onSave={save} saveDisabled={selectedRows.length === 0 || missingDate || saving}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "min(60vh, 560px)" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* 입력칸은 화면 상단 필터와 같은 MUI 기본 크기 — 글자 크기를 따로 주지 않는다 */}
          <FormControl size="small" sx={{ width: 180 }}>
            <InputLabel>{t("llmUsageProvider")}</InputLabel>
            <Select label={t("llmUsageProvider")} value={provider}
              onChange={(e) => selectProvider(sources, e.target.value)}>
              {sources.map((s) => (
                <MenuItem key={s.provider_cd} value={s.provider_cd} disabled={!s.supported}>
                  {s.provider_cd}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* 취득 조건은 취득원이 알려준다 — 값 목록이 있으면 선택, 없으면 직접 입력 */}
          {regionOption && (regionOption.choices || []).length > 0 ? (
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>{t("region")}</InputLabel>
              <Select label={t("region")} value={region}
                onChange={(e) => { setRegion(e.target.value); setRegionInput(e.target.value); }}>
                {regionOption.choices.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          ) : regionOption ? (
            <TextField size="small" label={t("region")} value={regionInput} sx={{ width: 200 }}
              onChange={(e) => setRegionInput(e.target.value)}
              onBlur={() => setRegion(regionInput.trim())}
              onKeyDown={(e) => { if (e.key === "Enter") setRegion(regionInput.trim()); }} />
          ) : null}
        </Box>
        {source && !source.supported && (
          <Alert severity="info" sx={{ fontSize: FONT.BODY, py: 0 }}>{t("llmUsagePriceSourceUnsupported")}</Alert>
        )}
        {source?.source_error && (
          <Alert severity="warning" sx={{ fontSize: FONT.BODY, py: 0 }}>{t("llmUsagePriceSourceError")}</Alert>
        )}
        <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 200 }}>
          <AgGridReact ref={gridRef} rowData={rows} columnDefs={columnDefs} {...gridSetting} loading={loading}
            onSelectionChanged={(e) => setSelected(e.api.getSelectedNodes().map((n) => n.data.model_id))}
            onCellValueChanged={(e) => setRows((prev) => prev.map((r) => (
              r.model_id === e.data.model_id ? { ...r, effective_from: e.data.effective_from } : r)))} />
        </Box>
      </Box>
    </BaseConfigModal>
  );
};

export default LlmUsagePriceSuggestDialog;
